// Steve's: a classic A-frame tiki bar on a lava-rock plinth, at the north pole.
// Local frame: the deck top is y = DECK, the counter faces +z (where customers sit).
//
// Tiki signals, strongest first (see the research brief in CLAUDE.md): carved tiki posts,
// a steep thatched A-frame with an upswept ridge beam, glass floats in nets, pufferfish
// lamps, a bamboo bar front, tiki mugs, tapa cloth, a volcano bowl, torches, neon script.
import * as THREE from 'three';
import * as T from './textures.js';
import { PALETTE, pbr, surface, woodSet, bambooSet, thatchSet, lavaSet, rattanSet, tapaTexture, glow } from './materials.js';
import { carvedTiki, glassFloat, pufferLamp, tikiMug, volcanoBowl, tikiTorch, palm, lavaRock, barGlass, garnishFor, coconut } from './props.js';
import { place, BAR_DIR, surfaceRadius } from './planet.js';
import { heroOr, heroSlot } from './hero.js';
import { createFire } from './fire.js';
import { tinRobot } from './robot.js';
import * as D from './decor.js';

const TAU = Math.PI * 2;

// The bar sits at the pole with no rotation, so its local axes are world axes. Off the deck
// the planet curves away: this is the local y of the real ground at (x, z).
const R0 = surfaceRadius(BAR_DIR);
function groundY(x, z) {
  const rg = surfaceRadius(new THREE.Vector3(x, R0, z).normalize());
  return Math.sqrt(Math.max(0, rg * rg - x * x - z * z)) - R0;
}
const DECK = 0.25;      // deck surface height above the ground
const BAR_TOP = DECK + 0.95;

function mesh(geo, material, [x, y, z] = [0, 0, 0], parent, { cast = true, receive = true } = {}) {
  const m = new THREE.Mesh(geo, material);
  m.position.set(x, y, z);
  m.castShadow = cast;
  m.receiveShadow = receive;
  parent && parent.add(m);
  return m;
}

export function buildBar({ prop, quality, signName = "Steve's", board: boardText = { title: 'Favorite drinks', names: [], verb: 'Read the recipes' }, heroes, reducedMotion = false }) {
  const bar = new THREE.Group();
  bar.name = 'bar';
  const animated = [];
  const M = {
    deck: surface(woodSet(0x5a3a24), { repeat: [3, 3], roughness: 0.8 }),
    beam: surface(woodSet(PALETTE.stain), { roughness: 0.75 }),
    top: surface(woodSet(0x3a2214), { roughness: 0.42, bumpScale: 0.15 }), // lacquered bar top
    bamboo: surface(bambooSet(), { roughness: 0.6 }),
    thatch: surface(thatchSet(), { roughness: 1, bumpScale: 3, side: THREE.DoubleSide }),
    lava: surface(lavaSet(), { bumpScale: 3, roughness: 0.95 }),
    rattan: surface(rattanSet(), { roughness: 0.7 }),
  };

  // Generated hero props don't come with lights or fire; add them.
  // A hanging lamp: the model, a cord up to the beam `hang` metres above, a light inside.
  const litHero = (slot, intensity, hang) => {
    const g = new THREE.Group();
    const h = heroOr(heroes, slot);
    g.add(h);
    const top = new THREE.Box3().setFromObject(h).max.y;
    h.position.y -= top / 2; // centred on the group, like the procedural lamp
    mesh(new THREE.CylinderGeometry(0.006, 0.006, hang, 5), pbr({ color: PALETTE.lava, roughness: 0.9 }), [0, top * 0.45 + hang / 2, 0], g, { cast: false });
    g.add(new THREE.PointLight(PALETTE.amber, intensity, 5.5, 1.6));
    return g;
  };
  const firedHero = (slot) => {
    const g = new THREE.Group();
    const h = heroOr(heroes, slot);
    g.add(h);
    const top = new THREE.Box3().setFromObject(h).max.y;
    const fire = createFire({ width: 0.09, height: 0.16, light: 1.2, distance: 3 });
    fire.group.position.y = top * 0.9;
    g.add(fire.group);
    g.userData.update = fire.update;
    return g;
  };

  // The mug set: the teal tiki mug, a parrot mug and a pineapple mug (generated hero models),
  // each falling back to the procedural mug. Drinks, the shelf and the counter all draw on it.
  // They load after the reveal (they're small from where you land), the procedural mug standing in
  // until then; `now` (a drink being served, fitted to its mug) takes whichever is there and keeps it.
  const MUGS = ['tiki-mug', 'parrot-mug', 'pineapple-mug'];
  const mugFallback = [PALETTE.teal, PALETTE.coral, PALETTE.amber];
  const anyMug = (i, { garnish = true, now = false } = {}) => {
    const slot = MUGS[i % MUGS.length];
    const stand = () => tikiMug({ glaze: mugFallback[i % MUGS.length], shape: i % 2 ? 'moai' : 'tall', garnish });
    return now ? heroOr(heroes, slot, stand) : heroSlot(slot, new THREE.Group().add(stand()));
  };

  /* ---------- Plinth and deck ---------- */
  // deep enough that its edge sinks into the ground where the planet curves away
  mesh(new THREE.CylinderGeometry(3.7, 3.9, DECK + 0.7, 40), M.deck, [0, (DECK - 0.7) / 2, 0.2], bar);
  // lava-rock rim
  for (let i = 0; i < 30; i++) {
    const a = (i / 30) * TAU;
    if (Math.abs(a - Math.PI / 2) < 0.42) continue; // leave the entrance open where the path arrives
    const rock = lavaRock({ size: 0.24 + (i % 3) * 0.05, seed: i + 3 });
    const rx = Math.cos(a) * 3.85, rz = Math.sin(a) * 3.85 + 0.2;
    rock.position.set(rx, groundY(rx, rz) + 0.05, rz);
    rock.rotation.y = a * 3;
    bar.add(rock);
  }

  /* ---------- Carved tiki posts hold up the roof ---------- */
  const POSTS = [[-2.45, 1.55, 'marquesan'], [2.45, 1.55, 'marquesan'], [-2.45, -1.6, 'ku'], [2.45, -1.6, 'ku']];
  const EAVE = DECK + 2.7;
  POSTS.forEach(([x, z, style]) => {
    const post = heroOr(heroes, 'tiki-post', () => carvedTiki({ height: EAVE - DECK, radius: 0.24, style }));
    post.position.set(x, DECK, z);
    post.rotation.y = z > 0 ? 0 : Math.PI; // faces point outward, front and back
    bar.add(post);
  });
  // eave beams along both sides, tie beams front and back
  for (const x of [-2.45, 2.45]) mesh(new THREE.BoxGeometry(0.22, 0.22, 4.4), M.beam, [x, EAVE + 0.05, -0.02], bar);
  for (const z of [1.55, -1.6]) mesh(new THREE.BoxGeometry(5.2, 0.18, 0.18), M.beam, [0, EAVE, z], bar);

  /* ---------- Steep A-frame thatched roof, gable facing the customer ---------- */
  const RIDGE = EAVE + 3.1, HALF = 3.05, Z0 = -2.5, Z1 = 2.6;
  {
    const slope = Math.hypot(HALF, RIDGE - EAVE);
    const ang = Math.atan2(RIDGE - EAVE, HALF);
    for (const s of [-1, 1]) {
      const plane = mesh(new THREE.BoxGeometry(slope + 0.4, 0.28, Z1 - Z0), M.thatch, [s * HALF / 2, (EAVE + RIDGE) / 2 - 0.05, (Z0 + Z1) / 2], bar);
      plane.rotation.z = -s * ang;
    }
    // ridge beam, sticking out past the gable and sweeping up at the front
    mesh(new THREE.BoxGeometry(0.26, 0.26, Z1 - Z0 + 0.6), M.beam, [0, RIDGE + 0.05, (Z0 + Z1) / 2 - 0.1], bar);
    const sweep = mesh(new THREE.BoxGeometry(0.24, 0.24, 1.5), M.beam, [0, RIDGE + 0.35, Z1 + 0.85], bar);
    sweep.rotation.x = -0.45;
    // Googie starburst on the tip: the space-age half of Polynesian Pop
    const star = new THREE.Group();
    star.position.set(0, RIDGE + 0.72, Z1 + 1.55);
    bar.add(star);
    const rayMat = glow(PALETTE.aqua, 3.2);
    for (let i = 0; i < 8; i++) {
      const ray = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, i % 2 ? 0.38 : 0.62, 4), rayMat);
      ray.rotation.z = (i / 8) * Math.PI;
      star.add(ray);
    }
    star.add(new THREE.Mesh(new THREE.SphereGeometry(0.06, 12, 8), glow(0xffffff, 4)));
    animated.push((t) => { star.rotation.z = t * 0.25; });
    // frayed thatch fringe hanging off both eaves
    const strands = new THREE.InstancedMesh(new THREE.ConeGeometry(0.05, 0.55, 3).translate(0, -0.27, 0), pbr({ color: PALETTE.thatch, roughness: 1 }), 120);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion();
    for (let i = 0; i < 120; i++) {
      const s = i < 60 ? -1 : 1, k = (i % 60) / 59;
      const len = 0.8 + Math.sin(i * 7.1) * 0.3;
      q.setFromEuler(new THREE.Euler(0, 0, s * 0.35 + Math.sin(i * 3.3) * 0.1));
      strands.setMatrixAt(i, m.compose(new THREE.Vector3(s * (HALF + 0.12), EAVE - 0.05, Z0 + k * (Z1 - Z0)), q, new THREE.Vector3(1, len, 1)));
    }
    strands.castShadow = true;
    bar.add(strands);
  }

  /* ---------- The sign in the gable: the planet's sign ("Steve's") in neon script over a lit "World famous" box ---------- */
  {
    const sign = new THREE.Group();
    sign.position.set(0, EAVE + 1.25, Z1 - 0.05);
    bar.add(sign);
    // dark plank backing cut to the gable's triangle
    const tri = new THREE.Shape();
    tri.moveTo(-2.6, -1.05); tri.lineTo(2.6, -1.05); tri.lineTo(0, 1.75); tri.closePath();
    mesh(new THREE.ShapeGeometry(tri), surface(woodSet(0x2a180e), { roughness: 0.9, side: THREE.DoubleSide }), [0, 0, -0.02], sign);
    const face = new THREE.Mesh(new THREE.PlaneGeometry(3.3, 1.03), new THREE.MeshBasicMaterial({ map: T.neonSign(signName), transparent: true, toneMapped: false, depthWrite: false }));
    face.material.userData.keep = true;
    face.material.color.setScalar(1.35); // just into HDR: the bright tube cores bloom, the letters stay crisp
    face.position.set(0, -0.15, 0.03);
    sign.add(face);
    // Under the script, a lit box: "World famous" in dark letters on a glowing face, framed in
    // dark wood with a brass rim and a row of marquee bulbs top and bottom.
    const box = new THREE.Group();
    box.position.set(0, -0.64, 0.05);
    sign.add(box);
    const BW = 2.25, BH = 0.4; // as big as the gable allows, so it reads from where you land
    mesh(new THREE.BoxGeometry(BW + 0.16, BH + 0.16, 0.1), M.beam, [0, 0, -0.03], box);
    mesh(new THREE.BoxGeometry(BW + 0.06, BH + 0.06, 0.1), pbr({ color: PALETTE.brass, metalness: 0.55, roughness: 0.35 }), [0, 0, -0.02], box);
    const lit = new THREE.Mesh(new THREE.PlaneGeometry(BW, BH), new THREE.MeshBasicMaterial({ map: T.signBox('WORLD FAMOUS'), toneMapped: false }));
    lit.material.userData.keep = true;
    lit.material.color.setScalar(0.95); // bright, but under the bloom threshold, so the letters stay sharp
    lit.position.z = 0.031;
    box.add(lit);
    const bulbs = new THREE.InstancedMesh(new THREE.SphereGeometry(0.022, 10, 8), glow(PALETTE.amber, 2.8), 26);
    for (let i = 0; i < 13; i++) {
      const x = -BW / 2 - 0.02 + (i / 12) * (BW + 0.04);
      bulbs.setMatrixAt(i, new THREE.Matrix4().makeTranslation(x, BH / 2 + 0.08, 0.035));
      bulbs.setMatrixAt(13 + i, new THREE.Matrix4().makeTranslation(x, -BH / 2 - 0.08, 0.035));
    }
    box.add(bulbs);
    const neonLight = new THREE.PointLight(PALETTE.hibiscus, 3, 7, 1.6);
    neonLight.position.set(0, EAVE + 0.9, Z1 + 1.3);
    bar.add(neonLight);
    animated.push((t) => {
      const on = Math.sin(t * 0.7) > 0.985 ? 0.2 : 1;
      face.material.opacity = on;
      neonLight.intensity = 3 * on;
    });
  }

  /* ---------- Warm spill out of the front, so the bar glows from down the path ---------- */
  {
    const spill = new THREE.PointLight(PALETTE.amber, 4.5, 9, 1.4);
    spill.position.set(0, DECK + 2.2, 1.9);
    bar.add(spill);
  }

  /* ---------- Counter ---------- */
  {
    const counter = new THREE.Group();
    counter.position.set(0, DECK, 0.55);
    bar.add(counter);
    mesh(new THREE.BoxGeometry(3.8, 0.85, 0.62), M.beam, [0, 0.425, 0], counter);
    const n = 30;
    const slats = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.063, 0.063, 0.86, 10), M.bamboo, n);
    const m = new THREE.Matrix4();
    for (let i = 0; i < n; i++) slats.setMatrixAt(i, m.makeTranslation(-1.85 + i * (3.7 / (n - 1)), 0.43, 0.33));
    slats.castShadow = slats.receiveShadow = true;
    counter.add(slats);
    mesh(new THREE.BoxGeometry(4.15, 0.09, 0.92), M.top, [0, 0.9, 0.08], counter);
    // a warm strip of light under the bar-top lip
    mesh(new THREE.BoxGeometry(3.9, 0.02, 0.02), glow(PALETTE.amber, 2.2), [0, 0.84, 0.52], counter, { cast: false });
    // manila rope along the front edge of the bar top
    counter.add(D.rope([[-2.07, 0.9, 0.555], [2.07, 0.9, 0.555]], 0.022));
  }

  const boardGroup = new THREE.Group(); // the favorite-drinks chalkboard, hung on the back bar below
  let board = null; // its face and rows, for leaning in on it (below)

  /* ---------- Back bar: tapa-cloth wall, lit shelves of bottles and mugs ---------- */
  {
    const back = new THREE.Group();
    back.position.set(0, DECK, -1.45);
    bar.add(back);
    // bamboo wall with a tapa panel
    const wall = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.07, 0.07, 2.6, 8), M.bamboo, 34);
    const m = new THREE.Matrix4();
    for (let i = 0; i < 34; i++) wall.setMatrixAt(i, m.makeTranslation(-2.3 + i * 0.14, 1.3, -0.32));
    wall.receiveShadow = true;
    back.add(wall);
    mesh(new THREE.PlaneGeometry(3.6, 0.9), pbr({ map: tapaTexture(), roughness: 0.95 }), [0, 2.1, -0.24], back);
    for (const y of [0.95, 1.5]) {
      mesh(new THREE.BoxGeometry(3.6, 0.06, 0.34), M.beam, [0, y, 0], back);
      mesh(new THREE.BoxGeometry(3.5, 0.015, 0.015), glow(PALETTE.amber, 2.4), [0, y - 0.04, 0.16], back, { cast: false });
    }
    // A rum wall, backlit amber so the bottles glow in silhouette (Smuggler's Cove, Three
    // Dots): two rows on the lower shelf, a row behind the mugs, and a short top shelf over
    // the robot's station. The chalkboard keeps its wall clear.
    D.backlight(back, { y: 0.98, z: -0.2, width: 3.5, height: 0.36 });
    const upperGlow = D.backlight(back, { y: 1.53, z: -0.2, width: 2.1, height: 0.3 });
    upperGlow.position.x = 0.7;
    mesh(new THREE.BoxGeometry(1.0, 0.05, 0.24), M.beam, [0.6, 2.0, -0.05], back);
    mesh(new THREE.BoxGeometry(0.95, 0.012, 0.012), glow(PALETTE.amber, 2.4), [0.6, 1.965, 0.07], back, { cast: false });
    D.rumWall(back, [
      { y: 0.98, z: -0.09, x0: -1.72, x1: 1.72 },
      { y: 0.98, z: 0.07, x0: -1.66, x1: 1.7, gapsAt: [-0.55, 0.6] },
      { y: 1.53, z: -0.1, x0: -0.35, x1: 1.72 },
      { y: 2.025, z: -0.05, x0: 0.16, x1: 1.05 },
    ]);
    // crossed outrigger paddles on the tapa, and a small carved mask left of the chalkboard
    const paddles = D.crossedPaddles();
    paddles.position.set(1.5, 2.25, -0.2);
    back.add(paddles);
    const mask = carvedTiki({ height: 0.5, radius: 0.13, style: 'ku' });
    mask.position.set(-1.66, 1.93, -0.24);
    mask.scale.z = 0.45; // a flat plaque, not a post
    back.add(mask);
    for (let i = 0; i < 9; i++) {
      const mug = anyMug(i, { garnish: false });
      mug.position.set(-1.55 + i * 0.39, 1.53, 0.02);
      mug.scale.setScalar(1.4);
      back.add(mug);
    }
    // Favorite drinks (or whatever the planet's chalkboard shows), chalked on a board hung on the
    // back wall where you see it from your stool, to the robot's left. Faintly self-lit, so the
    // chalk reads in the dim bar.
    {
      const map = T.chalkboard(boardText.title, boardText.names);
      boardGroup.position.set(-0.95, 2.19, -0.2); // above the mugs on the top shelf
      boardGroup.rotation.x = 0.06; // hangs a touch forward
      back.add(boardGroup);
      mesh(new THREE.BoxGeometry(1.04, 0.8, 0.04), M.beam, [0, 0, -0.015], boardGroup);
      const face = mesh(new THREE.PlaneGeometry(0.96, 0.72), pbr({ map, emissiveMap: map, emissive: 0xffffff, emissiveIntensity: 0.35, roughness: 0.95 }), [0, 0, 0.007], boardGroup);
      face.castShadow = false;
      // a chalk underline under the drink you're on (main.js: lean in on the board, tap a name)
      const line = mesh(new THREE.PlaneGeometry(1, 0.008), new THREE.MeshBasicMaterial({ color: 0xffd36e, transparent: true, opacity: 0.9 }), [0, 0, 0.009], boardGroup);
      line.castShadow = line.receiveShadow = false;
      line.visible = false;
      const rows = map.userData.rows;
      board = {
        group: boardGroup,
        face,
        rows: rows.length,
        size: new THREE.Vector2(1.04, 0.8),
        /** The row (drink index) at a uv on the face, or -1. */
        rowAt(uv) {
          return rows.findIndex((r) => uv.y >= r.v0 && uv.y <= r.v1 && uv.x >= r.u0 - 0.1 && uv.x <= r.u1 + 0.04);
        },
        /** Underline row i (or none, -1). */
        mark(i) {
          const r = rows[i];
          line.visible = !!r;
          if (!r) return;
          line.scale.x = (r.u1 - r.u0) * 0.96 + 0.06;
          line.position.set(((r.u0 + r.u1) / 2 - 0.5) * 0.96, (r.base - 0.5) * 0.72, 0.009);
        },
      };
    }
    const shelfLight = new THREE.PointLight(PALETTE.amber, 1.4, 3.5, 2);
    shelfLight.position.set(0, 1.9, 0.6);
    back.add(shelfLight);
  }

  /* ---------- Hanging: glass floats in nets and pufferfish lamps ---------- */
  {
    const floats = [[-1.6, 1.0, 0.6, PALETTE.aqua], [-0.7, 0.2, 1.1, 0x4fa36a], [0.9, -0.4, 0.8, PALETTE.amber], [1.7, 0.9, 0.5, PALETTE.aqua], [0.1, 1.3, 1.4, 0x3f7fd0]];
    floats.forEach(([x, z, hang, c], i) => {
      const f = glassFloat({ radius: 0.14 + (i % 2) * 0.04, color: c, hang, lit: 2.6 });
      f.position.set(x, EAVE + 0.9 - hang, z);
      bar.add(f);
      animated.push((t) => { f.rotation.z = Math.sin(t * 0.6 + i) * 0.04; });
    });
    const heroPuffer = heroes && heroes.has('puffer-lamp');
    for (const x of [-0.95, 0.95]) {
      const p = heroPuffer ? litHero('puffer-lamp', quality.high ? 2.6 : 1.8, 0.9) : pufferLamp({ radius: 0.2, hang: 0.9, light: quality.high ? 2.6 : 1.8 });
      p.position.set(x, EAVE + 0.4 - 0.9, 0.7);
      // the procedural fish faces +x; generated ones face +z, so turn them in toward the seat
      p.rotation.y = heroPuffer ? (x > 0 ? -0.45 : 0.45) : (x > 0 ? Math.PI : 0);
      bar.add(p);
    }
  }

  /* ---------- Overhead: a net full of floats, grass over the front beam, festoon bulbs ---------- */
  {
    const net = D.ceilingNet({ width: 3.5, depth: 3.1, y: EAVE + 0.95, sag: 0.35, floats: quality.high ? 18 : 12 });
    net.position.z = 0.3;
    bar.add(net);
    bar.add(D.grassFringe({ x0: -2.3, x1: 2.3, y: EAVE - 0.08, z: 1.66, count: 240, length: 0.4 }));
    const bulbs = D.festoon([[-2.45, EAVE - 0.02, 1.74], [-0.82, EAVE - 0.02, 1.74], [0.82, EAVE - 0.02, 1.74], [2.45, EAVE - 0.02, 1.74]], { perSpan: 6, sag: 0.26 });
    bulbs.name = 'festoon'; // a holiday recolours them (world/holidays/)
    bar.add(bulbs);
  }

  /* ---------- On the bar ---------- */
  {
    // the robot's station: citrus, tins, jigger, bitters, swizzles and umbrellas, a towel
    const station = D.barStation();
    station.position.set(1.58, BAR_TOP, 0.42);
    bar.add(station);
    const bowl = heroes && heroes.has('volcano-bowl') ? firedHero('volcano-bowl') : volcanoBowl();
    bowl.position.set(0.85, BAR_TOP, 0.7);
    bowl.scale.setScalar(1.3);
    bar.add(bowl);
    animated.push((t) => bowl.userData.update(t));
    [[-1.35, 0.72, 1], [-1.05, 0.58, 2]].forEach(([x, z, i]) => { // a parrot and a pineapple
      const mug = anyMug(i);
      mug.position.set(x, BAR_TOP, z);
      mug.scale.setScalar(1.3);
      bar.add(mug);
    });
    const coconut = prop('food-kit_coconut-half', 0.55);
    coconut.position.set(-1.7, BAR_TOP, 0.62);
    bar.add(coconut);
  }

  /* ---------- The menu, propped up in front of your stool: click it to pick it up ---------- */
  // The whole card flies up to you; nothing of it stays on the bar. Its face is painted from the
  // HTML menu itself (menu.paint(), from main.js), so it is the same card before and after the
  // handoff: same layout, same proportions.
  const MENU_H = 0.24, MENU_LEAN = 1.1; // leans well back, so it never blocks the robot
  const MENU_ON_BAR = new THREE.Color(0.85, 0.68, 0.62), WHITE = new THREE.Color(1, 1, 1); // how the lamps tint it on the counter (measured)
  const menuCard = new THREE.Group();
  const menuStand = new THREE.Group();
  const menuLit = pbr({ roughness: 0.8, alphaTest: 0.5 }), menuFlat = new THREE.MeshBasicMaterial({ alphaTest: 0.5 }); // alphaTest: the rounded corners
  const menuFace = new THREE.Mesh(new THREE.PlaneGeometry(1, MENU_H), menuLit);
  const menuBack = mesh(new THREE.BoxGeometry(1, MENU_H, 0.006), M.beam, [0, MENU_H / 2, 0], menuCard);
  {
    menuStand.position.set(0.05, BAR_TOP, 1.0);
    menuStand.rotation.y = -0.08; // turned a touch toward the middle stool
    bar.add(menuStand);
    menuCard.rotation.x = -MENU_LEAN;
    menuStand.add(menuCard);
    menuFace.position.set(0, MENU_H / 2, 0.004);
    menuFace.castShadow = menuFace.receiveShadow = true;
    menuCard.add(menuFace);
    menuFace.scale.x = menuBack.scale.x = MENU_H * (2 / 3);
  }

  /* ---------- Rattan stools; the middle one is yours ---------- */
  const stools = [-1.15, 0, 1.15].map((x) => {
    const s = new THREE.Group();
    s.position.set(x, DECK, 1.42);
    bar.add(s);
    // the stand-in for the Meshy stool (hero slot "bar-stool", 0.8 m to the top of its seat)
    const look = heroSlot('bar-stool', new THREE.Group());
    look.rotation.y = x * 0.6; // not all turned the same way
    s.add(look);
    mesh(new THREE.CylinderGeometry(0.24, 0.2, 0.12, 20), M.rattan, [0, 0.74, 0], look);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + 0.4;
      const leg = mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.72, 6), M.bamboo, [Math.cos(a) * 0.14, 0.36, Math.sin(a) * 0.14], look);
      leg.rotation.set(Math.sin(a) * 0.12, 0, -Math.cos(a) * 0.12);
    }
    mesh(new THREE.TorusGeometry(0.15, 0.015, 6, 20), M.bamboo, [0, 0.28, 0], look).rotation.x = Math.PI / 2;
    return s;
  });

  /* ---------- Your stool sits in a pool of light, so the bar needs no label ---------- */
  {
    const lamp = new THREE.Group();
    lamp.position.set(stools[1].position.x, EAVE - 0.55, 1.5);
    bar.add(lamp);
    mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.55, 5), pbr({ color: PALETTE.lava }), [0, 0.3, 0], lamp, { cast: false });
    mesh(new THREE.ConeGeometry(0.16, 0.16, 16, 1, true), surface(bambooSet(), { roughness: 0.6, side: THREE.DoubleSide }), [0, 0, 0], lamp);
    mesh(new THREE.SphereGeometry(0.045, 12, 8), glow(PALETTE.amber, 4), [0, -0.05, 0], lamp, { cast: false });
    const pool = new THREE.SpotLight(PALETTE.amber, quality.high ? 9 : 6, 4.5, 0.62, 0.7, 1.4);
    pool.position.set(0, -0.06, 0);
    lamp.add(pool);
    pool.target.position.set(stools[1].position.x, DECK, 1.45);
    bar.add(pool.target);
  }

  /* ---------- Torches flanking the entrance and the back ---------- */
  // Planted in the ground just outside the rock rim (they stood at deck height out past the
  // plinth's edge, floating), poles long enough that the flames stay at the height they were.
  const TORCHES = [[-3.25, 2.75], [3.25, 2.75], [-3.55, -2.0], [3.55, -2.0]];
  TORCHES.forEach(([x, z], i) => {
    const y = groundY(x, z) - 0.1;
    const torch = tikiTorch({ height: DECK + 2.1 - y, light: i < 2 ? 2.6 : (quality.high ? 1.8 : 0) });
    torch.position.set(x, y, z);
    bar.add(torch);
    animated.push((t) => torch.userData.update(t));
  });

  /* ---------- The robot bartender: a 1950s tin toy on one wheel ---------- */
  const ROBOT_HOME = new THREE.Vector3(0.75, DECK, -0.75); // waiting by the back bar
  const ROBOT_SERVE = new THREE.Vector3(0.05, DECK, -0.55); // across the counter from your stool
  const robot = tinRobot({ hero: heroes && heroes.has('robot') ? heroOr(heroes, 'robot') : null, reducedMotion });
  robot.setHome(ROBOT_HOME, -0.55); // three-quarters on, so you see its face from the path
  bar.add(robot.group);

  /* ---------- Palms around the plinth ---------- */
  // [x, z, which way it leans, height]. The third leans in over the bar's left side, not out:
  // leaning out, its crown stood in the telescope's sight of the moon for part of the month
  // (main.js lookThroughScope; checked against every place the moon can be from there).
  const PALMS = [[-4.7, -2.6, 0.5, 6.2], [4.8, -1.8, 2.8, 5.4], [-5.2, 1.9, 1.1, 4.8], [4.4, 3.6, 1.2, 4.2]];
  PALMS.forEach(([x, z, ry, h], i) => {
    const p = palm({ height: h, lean: 0.3 + (i % 2) * 0.15, seed: i + 5 });
    p.position.set(x, groundY(x, z) - 0.1, z);
    p.rotation.y = ry;
    bar.add(p);
    const crown = p.userData.crown;
    animated.push((t) => { crown.rotation.z = Math.sin(t * 0.8 + i) * 0.035; crown.rotation.x = Math.cos(t * 0.6 + i) * 0.03; });
  });

  /* ---------- Planting: monstera, red ti and ferns around the plinth, the entrance left open ---------- */
  {
    const BEDS = [[-3.7, 3.35], [-4.4, -2.3], [4.5, -1.5], [-4.75, 1.6], [4.05, 3.4], [0.1, -4.25], [-2.6, -3.85], [2.75, -3.75]];
    bar.add(D.planting(BEDS.map(([x, z]) => [x, z, groundY(x, z) - 0.03])));
  }

  place(bar, BAR_DIR);
  bar.updateMatrixWorld(true);

  /* ---------- World-space helpers for the player ---------- */
  const toWorld = (x, y, z) => bar.localToWorld(new THREE.Vector3(x, y, z));
  // Coconuts fallen from the front palms, loose for the physics (physics.js; main.js adds them to
  // the scene, since they live in world space, not the bar's).
  const loose = [[4.9, 4.4], [3.7, 4.6], [-5.6, 2.9]].map(([x, z], i) => {
    const object = coconut(i + 3);
    object.position.copy(toWorld(x, groundY(x, z) + object.userData.radius * 0.9, z));
    return { object, radius: object.userData.radius, buoyancy: 1.6 };
  });
  // Your body at the middle stool: where your eyes are (and look) as you step up, sit and get up.
  const sx = stools[1].position.x;
  const seat = {
    eye: toWorld(sx, DECK + 1.25, 1.55),
    look: toWorld(0.05, DECK + 1.25, -0.55), // the robot, across the counter
    stand: toWorld(sx, DECK + 1.6, 1.9),      // standing just behind the stool
    dip: toWorld(sx, DECK + 1.16, 1.5),       // lowest point while sitting down, leaning in
    lean: toWorld(sx, DECK + 1.3, 1.42),      // leaning forward to push up off the stool
    barTop: toWorld(0.05, DECK + 0.9, 0.4),   // where your eyes go while you sit or stand
    served: new THREE.Vector3(0, BAR_TOP, 0.82),
  };

  const colliders = [
    // (height: how tall a thing thrown meets it, for physics.js; walkOnly: not a thing at all)
    { center: toWorld(-1.3, 0, 0.5), radius: 0.85, height: BAR_TOP }, { center: toWorld(0, 0, 0.5), radius: 0.85, height: BAR_TOP }, { center: toWorld(1.3, 0, 0.5), radius: 0.85, height: BAR_TOP },
    ...stools.map((st) => ({ center: toWorld(st.position.x, 0, st.position.z), radius: 0.28, height: DECK + 0.8 })),
    ...POSTS.map(([x, z]) => ({ center: toWorld(x, 0, z), radius: 0.3 })),
    ...PALMS.map(([x, z]) => ({ center: toWorld(x, 0, z), radius: 0.3 })),
    ...TORCHES.map(([x, z]) => ({ center: toWorld(x, 0, z), radius: 0.15 })),
    { center: toWorld(-1.2, 0, -1.2), radius: 1.2, walkOnly: true }, { center: toWorld(1.2, 0, -1.2), radius: 1.2, walkOnly: true }, // staff only
  ];

  const interactables = [
    // a tap sits you only on or close to your stool (`near`); anywhere else on the bar walks you up to it
    { id: 'seat', label: signName, verb: 'Sit at the bar', object: bar, point: toWorld(sx, DECK + 0.78, 1.42), approach: toWorld(0, 0, 2.35), radius: 2.6,
      near: (worldPoint) => { const p = bar.worldToLocal(worldPoint.clone()); return p.y < DECK + 1.3 && Math.hypot(p.x - sx, p.z - stools[1].position.z) < 0.55; } },
    // listed after the bar, so clicking the card itself picks it up (and walks you over if need be)
    { id: 'menu', label: 'Menu', verb: 'Pick up the menu', object: menuCard, point: toWorld(0.05, BAR_TOP + 0.1, 0.95), approach: toWorld(0, 0, 2.35), radius: 2.6 },
    { id: 'drinks', label: boardText.title, verb: boardText.verb, object: boardGroup, point: toWorld(-0.95, DECK + 2.19, -1.65), approach: toWorld(0, 0, 2.35), radius: 2.6 },
  ];

  // Serving: the robot shakes, then slides a fresh tiki mug across the counter to your seat.
  // Timing: shake starts at once, the mug appears at 0.9s and has arrived by 1.3s, when
  // `onServed(mug)` is called. Each section's drink is its own colour.
  // Keeps the last few on the bar.
  const drinks = [];
  const sliding = [];
  const ORDER = ['about', 'writing', 'lab', 'shelf', 'contact', 'resume', 'launch', 'campfire'];
  const LIQUID = [PALETTE.amber, PALETTE.coral, PALETTE.aqua];
  const SLIDE = 0.4;
  let sfx = () => {}; // sound effects (sound.js), once someone turns sound on
  function serve(id, onServed) {
    robot.shake(0.85);
    sfx('shake', 0.85);
    setTimeout(() => {
      const i = Math.max(0, ORDER.indexOf(id));
      // always the open-topped tiki mug, so you can look into it; the drink's colour changes
      const d = anyMug(0, { garnish: false, now: true });
      // The drink: a glossy surface just below the rim, so there's something to look into.
      // Fitted to the rim itself (the mug's highest vertices), not the bounding box, which
      // the handle pulls off-centre.
      d.updateMatrixWorld(true);
      const top = new THREE.Box3().setFromObject(d).max.y;
      const rim = new THREE.Box3(), v = new THREE.Vector3();
      d.traverse((m) => {
        if (!m.isMesh) return;
        const pos = m.geometry.attributes.position;
        for (let k = 0; k < pos.count; k++) { v.fromBufferAttribute(pos, k).applyMatrix4(m.matrixWorld); if (v.y > top * 0.94) rim.expandByPoint(v); }
      });
      const mouth = rim.getCenter(new THREE.Vector3()).setY(top);
      const radius = Math.min(rim.max.x - rim.min.x, rim.max.z - rim.min.z) * 0.5 * 0.8; // inside the rim's thickness
      const liquid = new THREE.Mesh(new THREE.CircleGeometry(radius, 28).rotateX(-Math.PI / 2),
        pbr({ color: LIQUID[i % LIQUID.length], emissive: LIQUID[i % LIQUID.length], emissiveIntensity: 0.12, roughness: 0.12 }));
      liquid.position.set(mouth.x, top * 0.88, mouth.z);
      d.add(liquid);
      d.userData.mouth = mouth; // top centre, in the mug's frame
      d.userData.height = top;
      // first one just left of the menu, where a phone's narrow portrait view still shows it
      const to = seat.served.clone().add(new THREE.Vector3([-0.2, 0.3, -0.46][drinks.length % 3], 0, (drinks.length % 2) * -0.08));
      d.scale.setScalar(1.3);
      d.rotation.y = 0.15; // mugs face +z: toward you
      bar.add(d);
      drinks.push(d);
      if (drinks.length > 5) bar.remove(drinks.shift());
      robot.pour(SLIDE + 0.25);
      if (reducedMotion) { d.position.copy(to); onServed && onServed(d); }
      else {
        // from the robot's side of the bar top, sliding to you
        const from = new THREE.Vector3(to.x * 0.4 + ROBOT_SERVE.x * 0.6, BAR_TOP, 0.3);
        d.position.copy(from);
        sliding.push({ d, from, to, k: 0, onServed });
      }
    }, 900);
  }

  /* ---------- Making a real drink, step by step ---------- */
  // make(recipe) has the robot roll up to the counter and build it in front of you: set out the
  // glass (with its ice), pour each ingredient from its own bottle (into a tin if it's shaken,
  // straight into the glass if not; the level rises by the measure), then shake and strain,
  // stir, or swizzle, garnish, and slide it over. `onStep(i, n, text)` narrates; `onDone()`
  // once it's in front of you. Returns { skip(), cancel() }. Timed on real frame time (dt).
  const MAKE_SPOT = new THREE.Vector3(0.05, DECK, -0.16); // up against the back of the counter
  // Where things stand on the counter, so each is under the hand that works it: the tin in the
  // middle (the left hand pours into it, the right picks it up to shake), and the glass by the
  // right hand to strain into, or by the left if it's built in the glass.
  const WORK = { shaken: new THREE.Vector3(-0.15, BAR_TOP, 0.34), built: new THREE.Vector3(0.14, BAR_TOP, 0.34) }; // the built one clear of the caddy
  const TIN = new THREE.Vector3(0.06, BAR_TOP, 0.36);
  const TIN_MOUTH = new THREE.Vector3(0, 0.13, 0);
  const OZ = { '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3 };
  const ozOf = (line) => {
    const m = /(\d+)?\s*([½¼¾⅓⅔])?\s*oz/.exec(line);
    return m && (m[1] || m[2]) ? (+(m[1] || 0) + (OZ[m[2]] || 0)) : 0;
  };
  const BOTTLES = [['coffee liqueur', 'coffee'], ['kahl', 'coffee'], ['espresso', 'espresso'], ['campari', 'campari'], ['chartreuse', 'chartreuse'],
    ['falernum', 'falernum'], ['vermouth', 'vermouth'], ['pineapple', 'pineapple'], ['lime', 'lime'], ['rum', 'rum'], ['gin', 'gin'], ['syrup', 'syrup'], ['sugar', 'syrup']];
  const bottleColor = (line) => PALETTE[(BOTTLES.find(([k]) => line.toLowerCase().includes(k)) || [0, 'amber'])[1]];
  // The bottles. Each line of a recipe is poured from its own bottle: the real one, a hero
  // model (`bottle-*`, loaded once the scene is up: setBottles), or until then a plain bottle
  // tinted to match. First match wins.
  const REAL = [
    [/planteray/, 'planteray'], [/campari/, 'campari'], [/chartreuse/, 'chartreuse'], [/meletti|coffee liqueur|kahl/, 'meletti'],
    [/espresso/, 'espresso'], [/\bgin\b/, 'gin'], [/vermouth/, 'vermouth'], [/bourbon/, 'bourbon'],
    [/dark rum/, 'appleton'], [/aged|jamaica/, 'smithcross'], [/light rum|agricole/, 'threestars'],
    [/falernum|allspice/, 'falernum'], [/passion/, 'passionfruit'], [/orange liqueur|curaçao|cointreau/, 'cointreau'],
    [/orgeat/, 'orgeat'], [/syrup|sugar/, 'demerara'], [/pineapple/, 'pineapple'], [/lime/, 'lime'], [/orange/, 'orange'],
  ];
  const models = new Map(); // the hero models, once they're here
  // a plain bottle, tinted: what it pours from until the real one has loaded
  const plainBottle = (color) => {
    const g = new THREE.Group();
    const glassMat = pbr({ color, roughness: 0.15, emissive: color, emissiveIntensity: 0.45 }); // lit from the back bar
    const add = (geo, m, y) => { const o = new THREE.Mesh(geo, m); o.position.y = y; o.castShadow = true; g.add(o); };
    add(new THREE.CylinderGeometry(0.034, 0.034, 0.16, 14), glassMat, 0.08);
    add(new THREE.CylinderGeometry(0.0345, 0.0345, 0.06, 14), pbr({ color: PALETTE.cream, roughness: 0.8 }), 0.08); // label
    add(new THREE.CylinderGeometry(0.012, 0.034, 0.05, 12), glassMat, 0.185);
    add(new THREE.CylinderGeometry(0.011, 0.012, 0.05, 10), glassMat, 0.235);
    add(new THREE.CylinderGeometry(0.013, 0.013, 0.02, 10), pbr({ color: PALETTE.chrome, metalness: 0.7, roughness: 0.3 }), 0.26);
    return g;
  };
  const heightOf = (o) => new THREE.Box3().setFromObject(o).getSize(new THREE.Vector3()).y;
  // the bottle a line pours from: { line, object, height, hero } (a copy if the drink uses it
  // twice), or a plain one (`plain`) until its model is here
  function bottleFor(line, used) {
    const r = REAL.find(([re]) => re.test(line.toLowerCase()));
    const hero = r && models.get('bottle-' + r[1]);
    if (!hero) return { line, object: plainBottle(bottleColor(line)), height: 0.27, plain: true, hero: null, coming: !!r }; // coming: it has a model, not here yet
    const object = used.has(hero) ? hero.clone(true) : hero;
    used.add(hero);
    return { line, object, height: hero.userData.height || (hero.userData.height = heightOf(hero)), plain: false, hero };
  }

  // The caddy: a 1950s revolving bottle caddy by the robot's left hand, holding the drink's
  // bottles in the order they're poured. It spins as the robot sets up and comes round with this
  // drink's bottles, and turns a slot between pours to bring the next one to its hand.
  const CADDY = new THREE.Vector3(0.56, BAR_TOP, 0.44), CADDY_R = 0.135, TRAY = 0.03;
  // to pour it rolls a little to its right and turns, so its left hand reaches the caddy, the tin
  // and the glass without crossing in front of itself (found by searching spots and turns for
  // arm poses that reach all three clear of the robot)
  const POUR_SPOT = MAKE_SPOT.clone().add(new THREE.Vector3(-0.15, 0, 0)), POUR_TURN = -0.2;
  // Each arm reaches straight out in front of its shoulder, so for the rest it rolls along the
  // counter until that shoulder is behind what it works on: the tin it picks up, the glass it
  // strains into and garnishes, the glass it sets out (`ARM_X`: found by solving the arms at
  // spots along the counter). Stirring and swizzling, the rod stood in a glass on its left, need
  // it further over and turned (`STIR`, found the same way).
  const ARM_X = 0.35;
  const reachFrom = (side, at) => new THREE.Vector3(at.x + (side === 'r' ? ARM_X : -ARM_X), DECK, MAKE_SPOT.z);
  const STIR = { spot: new THREE.Vector3(WORK.built.x - 0.64, DECK, MAKE_SPOT.z), turn: -0.35 };
  const caddy = (() => {
    const g = new THREE.Group(), spin = new THREE.Group();
    g.position.copy(CADDY);
    g.userData.noBatch = true; // it turns
    g.add(spin);
    bar.add(g);
    const brass = pbr({ color: PALETTE.amber, metalness: 0.75, roughness: 0.3 });
    const chromeM = pbr({ color: PALETTE.chrome, metalness: 0.8, roughness: 0.25 });
    const add = (parent, geo, m, y) => { const o = new THREE.Mesh(geo, m); o.position.y = y; o.castShadow = o.receiveShadow = true; parent.add(o); return o; };
    add(g, new THREE.CylinderGeometry(0.06, 0.075, 0.012, 24), chromeM, 0.006);                       // the foot it turns on
    add(spin, new THREE.CylinderGeometry(CADDY_R + 0.06, CADDY_R + 0.065, 0.02, 40), M.top, 0.02);   // a lacquered tray
    add(spin, new THREE.TorusGeometry(CADDY_R + 0.06, 0.006, 6, 48), brass, TRAY).rotation.x = Math.PI / 2;
    add(spin, new THREE.TorusGeometry(CADDY_R + 0.05, 0.004, 6, 48), brass, 0.07).rotation.x = Math.PI / 2; // the rail that keeps them standing
    for (let i = 0; i < 4; i++) {
      const rod = add(spin, new THREE.CylinderGeometry(0.003, 0.003, CADDY_R + 0.05, 6), brass, 0.07);
      rod.rotation.set(0, (i * Math.PI) / 2, Math.PI / 2);
      rod.position.set(Math.cos((i * Math.PI) / 2) * (CADDY_R + 0.05) / 2, 0.07, -Math.sin((i * Math.PI) / 2) * (CADDY_R + 0.05) / 2);
    }
    add(spin, new THREE.CylinderGeometry(0.009, 0.009, 0.26, 8), brass, 0.15);                        // the post
    add(spin, new THREE.TorusGeometry(0.03, 0.007, 8, 20), brass, 0.31);                               // and its carrying ring
    // facing the robot's left shoulder, standing where it makes drinks, turned to pour
    const FRONT = Math.atan2(POUR_SPOT.x + 0.33 * Math.cos(POUR_TURN) - CADDY.x, POUR_SPOT.z - 0.33 * Math.sin(POUR_TURN) - CADDY.z);
    let slots = [], from = 0, to = 0, k = 1, dur = 0.4, swap = null;
    const ease = (x) => { const c = 1.6; return 1 + (c + 1) * (x - 1) ** 3 + c * (x - 1) ** 2; }; // overshoots a little, and settles: it's on a bearing
    return {
      group: g,
      /** Put `items` ({ object, height }) round the ring, labels out, in pour order. */
      load(items) {
        for (const it of slots) if (it.object.parent === spin) spin.remove(it.object);
        const n = Math.max(items.length, 4);
        slots = items; // the same items the drink pours from, so a swap reaches both
        items.forEach((it, i) => { it.a = (i * Math.PI * 2) / n; this.replace(i); });
      },
      /** Slot `i`'s item has a new object (the real bottle has come): take `old` out, stand it in. */
      swap(i, old) {
        if (old && old.parent) old.parent.remove(old);
        this.replace(i);
      },
      get slots() { return slots; },
      get angle() { return spin.rotation.y; },
      get tallest() { return slots.reduce((h, s) => Math.max(h, s.height), 0); },
      /** The caddy's turn that puts slot `i` in front of the robot (always turning the same way). */
      frontTurn(i) {
        let th = FRONT - slots[i].a;
        while (th > to + 1e-3) th -= Math.PI * 2;
        while (th < to - Math.PI * 2 + 1e-3) th += Math.PI * 2;
        return th;
      },
      /** Turn to `th` over `seconds`; `then` runs halfway (a swap, while it's a blur). */
      turn(th, seconds = 0.4, then = null) {
        from = spin.rotation.y; to = th; dur = seconds; k = 0; swap = then;
        if (reducedMotion || seconds <= 0) { k = 1; spin.rotation.y = to; if (swap) { swap(); swap = null; } }
      },
      /** Where slot `i`'s bottle stands (the bar's frame), with the caddy turned to `th`. */
      base(i, th = to) {
        const a = slots[i].a + th;
        return new THREE.Vector3(CADDY.x + CADDY_R * Math.sin(a), CADDY.y + TRAY, CADDY.z + CADDY_R * Math.cos(a));
      },
      FRONT,
      /** Put a bottle back in its slot. */
      replace(i) {
        const it = slots[i], o = it.object, a = it.a;
        o.position.set(CADDY_R * Math.sin(a), TRAY, CADDY_R * Math.cos(a));
        o.quaternion.identity();
        o.rotation.y = a;
        o.traverse((m) => { m.userData.noBatch = true; });
        spin.add(o);
      },
      update(dt) {
        if (k >= 1) return;
        k = Math.min(1, k + dt / dur);
        spin.rotation.y = from + (to - from) * (swap ? k * k * (3 - 2 * k) : ease(k));
        if (swap && k >= 0.5) { swap(); swap = null; }
      },
    };
  })();
  // its arms keep out of the counter (the top and the front under it; see Counter above), and out
  // of the caddy's tray and rail but for the pincer reaching in for a bottle
  const CADDY_OUT = CADDY_R + 0.065;
  robot.setSolids([
    new THREE.Box3(new THREE.Vector3(-2.075, DECK + 0.855, 0.17), new THREE.Vector3(2.075, DECK + 0.945, 1.09)),
    new THREE.Box3(new THREE.Vector3(-1.9, DECK, 0.24), new THREE.Vector3(1.9, DECK + 0.85, 0.86)),
  ], [new THREE.Box3(new THREE.Vector3(CADDY.x - CADDY_OUT, BAR_TOP, CADDY.z - CADDY_OUT), new THREE.Vector3(CADDY.x + CADDY_OUT, BAR_TOP + 0.08, CADDY.z + CADDY_OUT))]);
  // the four the house is known for, as plain bottles until the real ones come
  caddy.load(['planteray', 'campari', 'chartreuse', 'meletti'].map((l) => bottleFor(l, new Set())));
  // Models arriving (setBottles): every plain bottle whose real one is now here is swapped for it,
  // in the caddy or in the drink about to be made, even mid-drink (not the one in its hand)
  function upgrade(items) {
    const used = new Set(items.map((it) => it.hero).filter(Boolean));
    items.forEach((it, i) => {
      if (!it.plain || (job && job.held === i && items === caddy.slots)) return;
      const b = bottleFor(it.line, used);
      if (b.plain) return;
      const old = it.object;
      Object.assign(it, b);
      if (caddy.slots === items) caddy.swap(i, old);
    });
  }
  function setBottles(heroes) {
    for (const [k, v] of heroes) if (k.startsWith('bottle-')) models.set(k, v);
    upgrade(caddy.slots);
    if (job && job.items && job.items !== caddy.slots) upgrade(job.items);
  }

  // One pour, as a path for the left pincer and the bottle in it: over the caddy, down to the
  // bottle, lift it out, arc it over the vessel turning its label to you, tip it to pour, a twist
  // to cut the drip, back, and set it down. `t` is seconds into the pour.
  const POUR = { hover: 0.25, turn: 0.25, above: 0.65, grip: 0.8, take: 0.86, lift: 1.05, arc: 1.5, tip: 1.7, pour: 2.2, twist: 2.4, back: 2.8, down: 2.95, end: 3.2 };
  const sm = (a, b, t) => { const x = THREE.MathUtils.clamp((t - a) / (b - a), 0, 1); return x * x * (3 - 2 * x); };
  const bez = (a, c, b, k) => a.clone().multiplyScalar((1 - k) ** 2).addScaledVector(c, 2 * k * (1 - k)).addScaledVector(b, k * k);
  const lerpA = (a, b, k) => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * k;
  function pourPath(P, t) {
    const { pick, above, hover, into, h, grip, start, front } = P; // where it takes it, over it, clear of them all, the vessel's mouth
    // how the bottle is turned: spun about itself (`yaw`), then tipped toward the vessel (`tilt`)
    let tilt = 0, yaw = front, at, open = 0.08;
    const away = new THREE.Vector3().subVectors(CADDY, into).setY(0).normalize(); // from the vessel toward the caddy
    const axis = new THREE.Vector3().crossVectors(away, UP).normalize();
    const quat = (tl, yw) => new THREE.Quaternion().setFromAxisAngle(axis, tl).multiply(new THREE.Quaternion().setFromAxisAngle(UP, yw));
    // where the pincer has to be for the neck to sit over the vessel, at a tilt
    const neckAt = (tl, yw, rise = 0) => into.clone().add(new THREE.Vector3(0, 0.07 + rise, 0)).sub(new THREE.Vector3(0, h - grip, 0).applyQuaternion(quat(tl, yw)));
    const PRE = 0.9, POURING = 2.05, FULL = 2.35, END = 0.7; // radians of tilt
    // up clear of the caddy while it turns, then down onto the bottle
    if (t < POUR.above - 0.2) at = start.clone().lerp(hover, sm(0, POUR.hover, t)), open = 0.4;
    else if (t < POUR.above) at = hover.clone().lerp(above, sm(POUR.above - 0.2, POUR.above, t)), open = 0.4;
    else if (t < POUR.grip) at = above.clone().lerp(pick, sm(POUR.above, POUR.grip, t)), open = 0.4;
    else if (t < POUR.lift) at = pick.clone().addScaledVector(UP, 0.13 * sm(POUR.take, POUR.lift, t)), open = 0.4 - 0.32 * sm(POUR.grip, POUR.take, t);
    else if (t < POUR.arc) {
      const k = sm(POUR.lift, POUR.arc, t);
      yaw = lerpA(front, 0, k); tilt = PRE * k;
      const end = neckAt(PRE, 0), lifted = pick.clone().addScaledVector(UP, 0.13);
      at = bez(lifted, lifted.clone().lerp(end, 0.5).addScaledVector(UP, 0.1), end, k);
    } else if (t < POUR.pour) {
      yaw = 0; tilt = t < POUR.tip ? PRE + (POURING - PRE) * sm(POUR.arc, POUR.tip, t) : POURING + (FULL - POURING) * sm(POUR.tip, POUR.pour, t);
      at = neckAt(tilt, 0);
    } else if (t < POUR.twist) {
      const k = sm(POUR.pour, POUR.twist, t);
      yaw = (Math.PI / 2) * k; tilt = FULL + (END - FULL) * k;
      at = neckAt(tilt, yaw, 0.03 * k);
    } else if (t < POUR.back) {
      const k = sm(POUR.twist, POUR.back, t);
      yaw = lerpA(Math.PI / 2, front, k); tilt = END * (1 - k);
      const s0 = neckAt(END, Math.PI / 2, 0.03);
      at = bez(s0, s0.clone().lerp(above, 0.5).addScaledVector(UP, 0.1), above, k);
    } else if (t < POUR.down + 0.08) at = above.clone().lerp(pick, sm(POUR.back, POUR.down, t)), open = 0.08 + 0.32 * sm(POUR.down, POUR.down + 0.08, t);
    else at = pick.clone().lerp(above, sm(POUR.down + 0.08, POUR.end, t)), open = 0.4; // let go, and lift clear
    return { at, quat: quat(tilt, yaw), carried: t >= POUR.take && t < POUR.down, pouring: t > POUR.tip - 0.05 && t < POUR.pour };
  }
  const SET = { rockscubes: 'A rocks glass, full of ice.', collinscrushed: 'A collins glass, packed with crushed ice.', coupe: 'A chilled coupe.' };
  const DUR = { set: 1.5, pour: POUR.end, shake: 2.8, strain: 2.0, stir: 2.6, swizzle: 2.8, garnish: 1.8, serve: 1.2 };
  const tinMesh = (() => {
    const g = new THREE.Group();
    const m = chromeTin();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.035, 0.13, 18, 1, true), m);
    body.position.y = 0.065;
    const floor = new THREE.Mesh(new THREE.CircleGeometry(0.035, 18).rotateX(-Math.PI / 2), m);
    floor.position.y = 0.002;
    g.add(body, floor);
    g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
    return g;
  })();
  function chromeTin() { return pbr({ color: PALETTE.chrome, metalness: 0.55, roughness: 0.3, side: THREE.DoubleSide }); } // brushed: full chrome only mirrors the dark
  const stream = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.004, 1, 8, 1, true).translate(0, -0.5, 0), pbr({ color: PALETTE.amber, emissive: PALETTE.amber, emissiveIntensity: 0.7, roughness: 0.1 }));
  stream.visible = false;
  bar.add(stream);
  const UP = new THREE.Vector3(0, 1, 0), _amber = new THREE.Color(PALETTE.amber);
  const flowing = (v) => { if (stream.visible !== v) { stream.visible = v; sfx('pour', v); } };
  function pourStream(fromWorld, to, color) {
    const from = bar.worldToLocal(fromWorld.clone());
    const d = to.clone().sub(from);
    // only while it's really over the glass: a pour never crosses the counter
    if (!fromWorld || Math.hypot(d.x, d.z) > 0.05 || d.y > -0.02) { flowing(false); return; }
    flowing(true);
    stream.material.color.set(color).lerp(_amber, 0.25); // backlit by the bar: even blackstrap glows amber in a thin stream
    stream.material.emissive.copy(stream.material.color);
    stream.position.copy(from);
    stream.quaternion.setFromUnitVectors(UP, d.clone().normalize().negate());
    stream.scale.set(1, d.length(), 1);
  }

  let job = null;
  const made = [];
  function make(recipe, { onStep, onDone } = {}) {
    if (job) job.cancel();
    const M = recipe.make || {};
    const kind = M.glass || 'rocks';
    const extra = (M.steps || []).map((o) => { const [verb, text] = Object.entries(o)[0]; return { do: verb, text }; });
    const shaken = extra.some((x) => x.do === 'shake');
    const amounts = recipe.build.map(ozOf);
    const total = amounts.reduce((a, b) => a + b, 0) || recipe.build.length;
    let acc = 0;
    const steps = [
      { do: 'set', text: M.set || SET[kind + (M.ice || '')] || 'A glass.' },
      ...recipe.build.map((line, i) => { acc += amounts[i] || total / recipe.build.length; return { do: 'pour', text: line, color: bottleColor(line), level: acc / total, slot: i }; }),
      ...extra,
    ];
    const glass = barGlass(kind);
    glass.color(PALETTE[M.color] ?? PALETTE.amber);
    glass.group.scale.setScalar(1.25); // like the mugs: a touch big, so it reads from the stool
    glass.group.position.copy(shaken ? WORK.shaken : WORK.built);
    const near = shaken ? 'r' : 'l'; // the hand by the glass
    const vesselTop = (o, mouth) => o.position.clone().add(mouth.clone().multiplyScalar(o.scale.x));
    const tin = tinMesh.clone();
    tin.position.copy(TIN);
    const garnishes = [].concat(M.garnish || []);
    // in front of you, just left of the menu, where even a phone's narrow view shows it whole
    const toStool = seat.served.clone().add(new THREE.Vector3(-0.14, 0, 0.1)); // clear of the mugs, which land further back
    let i = -1, t = 0, level = 0, finished = false;
    const setLevel = (k) => { level = k; glass.fill(k); };
    const END = { pour: 0.7, strain: 0.82, stir: 0.8, swizzle: 0.8 }; // fills are a share of the glass; the rest is dilution
    const J = {
      steps,
      // each step: what it starts, what it's done by the end (so skipping, or a long frame, lands right)
      enter(st) {
        const W = vesselTop(glass.group, glass.mouth);
        // over to pour, clear of the caddy; then to wherever the working hand reaches what it works on
        if (st.do === 'pour') robot.roll(POUR_SPOT, POUR_TURN, 0.5);
        else if (st.do === 'stir' || st.do === 'swizzle') robot.roll(STIR.spot, STIR.turn, 0.5);
        else if (st.do === 'shake') robot.roll(reachFrom('r', TIN), 0, 0.5);
        else if (st.do === 'strain' || st.do === 'garnish') robot.roll(reachFrom(near, W), 0, 0.5);
        if (st.do === 'set') {
          // the caddy spins and comes round with this drink's bottles, the first in front
          const used = new Set();
          J.items = recipe.build.map((line) => bottleFor(line, used));
          let th = caddy.FRONT;
          while (th > caddy.angle - Math.PI * 4) th -= Math.PI * 2;
          caddy.turn(th, 1.2, () => { caddy.load(J.items); J.loaded = true; });
          robot.roll(reachFrom(near, W), 0);
          robot.hold('none', { shaker: !shaken });
          const at = { at: W.clone().add(new THREE.Vector3(0, 0.03, 0)), pincer: true };
          robot.reach(near === 'r' ? at : null, near === 'l' ? at : null);
        } else if (st.do === 'pour') {
          // its bottle comes round to the front, and it's poured (pourPath)
          if (!J.loaded) { caddy.load(J.items); J.loaded = true; }
          const item = J.items[st.slot], h = item.height, grip = h * 0.55;
          st.th = caddy.frontTurn(st.slot);
          const pick = caddy.base(st.slot, st.th).addScaledVector(UP, grip);
          const start = robot.tip('l') ? bar.worldToLocal(robot.tip('l')) : pick.clone();
          const top = CADDY.y + TRAY + caddy.tallest + 0.06;
          st.P = { pick, grip, h, start, front: caddy.FRONT,
            above: pick.clone().addScaledVector(UP, h - grip + 0.05),
            hover: new THREE.Vector3(start.x, Math.max(top, start.y), start.z),
            into: shaken ? vesselTop(tin, TIN_MOUTH) : W };
          robot.hold('none', { shaker: !shaken });
          robot.reach(null, { at: st.P.hover }); // the other arm out of the way
        } else if (st.do === 'shake') {
          robot.hold('none', { shaker: false });
          robot.reach({ at: vesselTop(tin, TIN_MOUTH).add(new THREE.Vector3(0, 0.02, 0)), pincer: true }, null);
        } else if (st.do === 'strain') {
          robot.hold('none', { shaker: true });
          robot.reach({ at: W.clone().add(new THREE.Vector3(0, 0.09, -0.015)) }, null);
        } else if (st.do === 'stir' || st.do === 'swizzle') {
          robot.hold(st.do === 'stir' ? 'spoon' : 'swizzle', { shaker: !shaken });
          robot.reach(null, { at: glass.group.position.clone().add(new THREE.Vector3(0, glass.height * 1.25 * 0.6, 0)), wiggle: st.do });
        } else if (st.do === 'garnish') {
          st.left = garnishes.slice();
          st.next = 0;
        } else if (st.do === 'serve') {
          // the ones it made before move down the bar, to make room (the last three stay)
          made.forEach((g, n) => g.position.copy(seat.served).add(new THREE.Vector3(-0.62 - (made.length - 1 - n) * 0.16, 0, 0.02)));
          robot.hold('towel', { shaker: true });
          robot.pour(0.8);
          st.from = glass.group.position.clone();
        }
      },
      during(st, t, dur) {
        const pouring = t > 0.45 && t < dur - 0.3;
        if (st.do === 'set') { if (t > 0.45 && !glass.group.parent) { bar.add(glass.group); glass.ice(M.ice); if (M.ice) sfx('clink'); if (shaken) bar.add(tin); } }
        else if (st.do === 'pour') {
          // with reduced motion: three still moments, not the path between them
          const tt = reducedMotion ? (t < 0.5 ? POUR.take : t < dur - 0.3 ? (POUR.tip + POUR.pour) / 2 : POUR.end) : t;
          if (!st.turned && tt >= POUR.turn) { st.turned = true; caddy.turn(st.th, 0.35); }
          const q = pourPath(st.P, tt);
          if (q.carried && !st.held && !st.back) { robot.carry(J.items[st.slot].object, { height: st.P.h, grip: st.P.grip }); st.held = true; J.held = st.slot; }
          if (!q.carried && st.held) { putBack(st); }
          robot.drive(q.at, q.quat, q.open);
          if (q.pouring && st.held) pourStream(robot.tip('l') || new THREE.Vector3(), st.P.into, st.color); else flowing(false);
          if (!shaken && stream.visible) setLevel(Math.min(st.level * END.pour, level + (st.level * END.pour - level) * Math.min(1, J.dt * 3.5)));
        } else if (st.do === 'shake') {
          if (t > 0.6 && tin.parent) { bar.remove(tin); robot.hold('none', { shaker: true }); robot.shake(dur - 0.75); sfx('shake', dur - 0.75); } // once it's there
        } else if (st.do === 'strain') {
          if (pouring) pourStream(robot.tip('r') || new THREE.Vector3(), vesselTop(glass.group, glass.mouth), PALETTE[M.color] ?? PALETTE.amber); else flowing(false);
          if (stream.visible) setLevel(level + (END.strain - level) * Math.min(1, J.dt * 2.5));
        } else if (st.do === 'stir' || st.do === 'swizzle') {
          setLevel(level + (END[st.do] - level) * Math.min(1, J.dt));
          if (st.do === 'swizzle') glass.frost(Math.min(1, t / dur));
        } else if (st.do === 'garnish') {
          // one garnish after another: picked up, then set on the glass
          const each = dur / Math.max(1, st.left.length);
          const k = Math.floor(t / each);
          if (k !== st.shown && k < st.left.length) {
            st.shown = k;
            robot.hold('garnish', { object: garnishFor(st.left[k]), shaker: !shaken, side: near });
            const at = { at: vesselTop(glass.group, glass.mouth).add(new THREE.Vector3(0, 0.05, -0.01)), pincer: true };
            robot.reach(near === 'r' ? at : null, near === 'l' ? at : null);
          }
          while (st.next < st.left.length && t > (st.next + 0.65) * each) { placeGarnish(st.left[st.next++]); robot.hold('none', { shaker: true }); }
        } else if (st.do === 'serve') {
          const k = THREE.MathUtils.clamp((t - 0.3) / SLIDE, 0, 1);
          const e = 1 - Math.pow(1 - k, 3);
          glass.group.position.lerpVectors(st.from, toStool, reducedMotion ? (t > 0.3 ? 1 : 0) : e);
        }
      },
      finish(st) {
        flowing(false);
        if (st.do === 'set') { if (!glass.group.parent) { bar.add(glass.group); glass.ice(M.ice); if (shaken) bar.add(tin); } if (!J.loaded) { caddy.load(J.items); J.loaded = true; caddy.turn(caddy.frontTurn(0), 0); } }
        else if (st.do === 'pour') { if (!shaken) setLevel(st.level * END.pour); if (st.held) putBack(st); if (!st.turned) { st.turned = true; caddy.turn(st.th, 0); } }
        else if (st.do === 'shake') { bar.remove(tin); }
        else if (st.do === 'strain') { setLevel(END.strain); if (M.foam) glass.foam(PALETTE[M.foam]); }
        else if (st.do === 'stir') setLevel(END.stir);
        else if (st.do === 'swizzle') { setLevel(END.swizzle); glass.frost(1); }
        else if (st.do === 'garnish') { while (st.next < st.left.length) placeGarnish(st.left[st.next++]); if (M.ice === 'crushed') glass.mound(); }
        else if (st.do === 'serve') glass.group.position.copy(toStool);
      },
      advance(dtStep) {
        if (finished) return;
        J.dt = dtStep;
        if (J.frozen) return J.during(J.all[i], t, DUR[J.all[i].do]); // held on one step (tests)
        t += dtStep;
        // the glass is out: give the drink's real bottles a moment to arrive (up to six seconds) before
        // the first pour, so it isn't made from plain stand-ins on a slow connection
        if (i >= 0 && J.all[i].do === 'set' && t >= DUR.set && J.items && J.items.some((it) => it.plain && it.coming) && (J.waited = (J.waited || 0) + dtStep) < 6) {
          t = DUR.set - 1e-3;
          return J.during(J.all[i], t, DUR.set);
        }
        while (i < 0 || t >= DUR[J.all[i].do]) {
          if (i >= 0) { t -= DUR[J.all[i].do]; J.finish(J.all[i]); }
          i++;
          if (i >= J.all.length) return J.done();
          const st = J.all[i];
          J.enter(st);
          if (st.do !== 'serve') onStep && onStep(i, steps.length, st.text);
        }
        J.during(J.all[i], t, DUR[J.all[i].do]);
      },
      done() {
        finished = true; job = null;
        made.push(glass.group);
        if (made.length > 3) bar.remove(made.shift());
        robot.reach(null, null);
        robot.roll(ROBOT_SERVE, Math.atan2(stools[1].position.x - ROBOT_SERVE.x, stools[1].position.z - ROBOT_SERVE.z));
        onDone && onDone(glass.group);
      },
      skip() {
        if (finished) return;
        // everything it would have done, at once, then slide it over
        for (let k = Math.max(0, i); k < J.all.length - 1; k++) { if (k > i) J.enter(J.all[k]); J.finish(J.all[k]); }
        i = J.all.length - 1; t = 0;
        J.enter(J.all[i]);
      },
      cancel() {
        if (finished) return;
        finished = true; job = null;
        flowing(false);
        bar.remove(glass.group, tin);
        if (J.held !== undefined && J.held !== null) { robot.carry(null); caddy.replace(J.held); J.held = null; }
        robot.reach(null, null);
        robot.hold('towel', { shaker: true });
      },
    };
    const placeGarnish = (name) => glass.garnish(name);
    // a bottle back in its slot in the caddy
    function putBack(st) { robot.carry(null); caddy.replace(st.slot); st.held = false; st.back = true; J.held = null; }
    // Where to look to see what it's doing now (world space): the vessel it's pouring into,
    // the shaker while it shakes, the glass for the rest, then the drink as it comes to you.
    const _f = new THREE.Vector3();
    // Framed so you see the hand and what it's working on together: between the two.
    const _h = new THREE.Vector3();
    J.focus = () => {
      const st = J.all[Math.max(0, Math.min(i, J.all.length - 1))];
      const vessel = st.do === 'pour' && shaken ? bar.localToWorld(_f.copy(tin.position).add(new THREE.Vector3(0, 0.1, 0)))
        : bar.localToWorld(_f.copy(glass.group.position).add(new THREE.Vector3(0, st.do === 'serve' ? 0.06 : 0.1, 0)));
      const hand = st.do === 'shake' && !tin.parent ? 'r' : st.do === 'pour' || st.do === 'stir' || st.do === 'swizzle' ? 'l' : st.do === 'strain' ? 'r' : null;
      const tip = hand && robot.tip(hand);
      if (!tip) return vessel;
      return vessel.lerp(_h.copy(tip), st.do === 'shake' ? 0.6 : 0.4);
    };
    // the middle of the work: where you lean in toward
    J.center = bar.localToWorld(glass.group.position.clone().lerp(TIN, 0.5).setY(BAR_TOP + 0.1));
    J.all = [...steps, { do: 'serve' }];
    job = J;
    return {
      skip: () => J.skip(),
      cancel: () => J.cancel(),
      /** For tests: jump to step `k`, as if the ones before it had been done, and hold it there, `at` seconds in (later on the same step: further on). */
      seek(k, at = 0.6) {
        if (J.frozen && k === i) { t = at; J.during(J.all[k], t, DUR[J.all[k].do]); return; }
        J.frozen = true;
        for (let n = Math.max(0, i); n < k; n++) { if (n > i) J.enter(J.all[n]); J.finish(J.all[n]); }
        i = k; t = at;
        J.enter(J.all[k]);
        if (J.all[k].do !== 'serve') onStep && onStep(k, steps.length, J.all[k].text);
        J.during(J.all[k], t, DUR[J.all[k].do]);
      },
      get steps() { return J.all.map((x) => x.do); },
      focus: () => J.focus(),
      center: J.center,
    };
  }

  return {
    group: bar,
    seat,
    colliders,
    // the plinth, for things that roll into it (physics.js)
    solids: [{ center: toWorld(0, (DECK - 0.7) / 2, 0.2), up: BAR_DIR.clone(), radius: 3.8, half: (DECK + 0.7) / 2 }],
    interactables,
    loose,
    serve,
    make,
    /** The favorite-drinks chalkboard: { group, face, rows, size, rowAt(uv), mark(i) }. */
    board,
    setBottles,
    /** The bottle models a drink's lines pour from (`bottle-*`), or with none, the four the caddy starts with. */
    bottleKeys(lines = []) {
      return (lines.length ? lines : ['planteray', 'campari', 'chartreuse', 'meletti']).map((l) => REAL.find(([re]) => re.test(l.toLowerCase()))).filter(Boolean).map(([, k]) => 'bottle-' + k);
    },
    /** For tests: what's in the caddy, line by line, and whether it's the real bottle yet. */
    caddySlots: () => caddy.slots.map((it) => ({ line: it.line, real: !it.plain })),
    /** Where sound effects go (sound.js `play`). */
    setSfx(fn) { sfx = fn; },
    robot,
    /** The menu card on the bar (what gets picked up) and a point just above it for its label. */
    menu: {
      card: menuCard,
      label: toWorld(0.05, BAR_TOP + 0.12, 0.9),
      height: MENU_H,
      /** New face, painted from the HTML card: `canvas` and its `aspect` (width / height). */
      paint({ canvas, aspect }) {
        const old = menuLit.map;
        menuLit.map = menuFlat.map = T.canvasTexture(canvas);
        menuLit.needsUpdate = menuFlat.needsUpdate = true;
        if (old) old.dispose();
        menuFace.scale.x = menuBack.scale.x = MENU_H * aspect;
      },
      /**
       * 0 = lit by the bar; above 0 it's drawn flat, the page's own colours, from roughly how it
       * looks on the counter (k near 0) to exactly the page (1), for the overlay pass in main.js.
       */
      light(k) {
        menuFace.material = k > 0 ? menuFlat : menuLit;
        menuBack.visible = !(k > 0); // unlit in the overlay it would be a black edge; the card is thin
        menuFlat.color.copy(MENU_ON_BAR).lerp(WHITE, k);
      },
    },
    /** The guest sat down: the robot rolls over to face them. */
    greet() { robot.greet(ROBOT_SERVE, new THREE.Vector3(stools[1].position.x, DECK, stools[1].position.z)); },
    /** The guest left: back to its spot. */
    farewell() { robot.idle(); },
    /** Someone's walking up (a world point): turn to them and wave them over. */
    beckon(worldPoint) { robot.beckon(bar.worldToLocal(worldPoint.clone())); },
    // t: ambient time (frozen under ?test); dt: real frame time, for things people cause
    update(t, dt = 0) {
      for (const f of animated) f(t);
      if (job) job.advance(dt);
      caddy.update(dt);
      robot.watch(job ? job.focus() : null); // its eyes on the work
      robot.update(dt, t);
      for (let i = sliding.length - 1; i >= 0; i--) {
        const s = sliding[i];
        s.k = Math.min(1, s.k + dt / SLIDE);
        const e = 1 - Math.pow(1 - s.k, 3); // decelerates, like a glass sliding on lacquer
        s.d.position.lerpVectors(s.from, s.to, e);
        if (s.k === 1) { sliding.splice(i, 1); s.onServed && s.onServed(s.d); }
      }
    },
  };
}
